<template>
  <vx-card title="Procesos no confiables" id="card">
    <!-- LOADING -->
    <div
      v-if="uiState === 'loading'"
      class="gw-empty-state"
      style="height:350px;"
    >
      <div class="gw-spinner" aria-label="Cargando"></div>
      <div class="gw-empty-title">Cargando información…</div>
      <div class="gw-empty-subtitle">Un momento por favor</div>
    </div>

    <!-- READY (CHART) -->
    <VueApexCharts
      v-else-if="uiState === 'ready' && renderGraphic"
      type="donut"
      height="350"
      :options="chartOptions"
      :series="series"
    />

    <!-- EMPTY / ERROR -->
    <div
      v-else
      class="gw-empty-state"
      style="height:350px;"
    >
      <img
        class="gw-empty-img"
        :src="emptyStateImage"
        :alt="emptyStateAlt"
      />
      <div class="gw-empty-title">{{ emptyStateTitle }}</div>
      <div class="gw-empty-subtitle">{{ emptyStateSubtitle }}</div>
    </div>
  </vx-card>
</template>

<script>
import { publicAssetUrl } from '@/utils/publicAssets'
import { eventBus } from "./../../main";
import VueApexCharts from "vue-apexcharts";
import api from "../../mixins/api";
import { mapGetters } from "vuex";

export default {
  props: ["startDate", "endDate", "cityId"],
  mixins: [api],

  data() {
    return {
      // UI states: 'loading' | 'ready' | 'empty' | 'error'
      uiState: "loading",
      renderGraphic: false,

      requestKey: null,
      consultHandler: null,

      // Empty/Error illustrations (S3)
      IMG_EMPTY:
        publicAssetUrl('Assets/illustrations/no_data_found.png'),
      IMG_ERROR:
        publicAssetUrl('Assets/illustrations/error.png'),

      series: [],

      chartOptions: {
        labels: [],
        colors: [
          "#ffc300",
          "#5F0091",
          "#c88170",
          "#c5c870",
          "#5eab2c",
          "#ff3200",
          "#0d2cc8",
          "#c20dc8",
          "#bf0093",
          "#616a6b",
          "#0e6251",
          "#0e6251",
          "#d35400",
          "#17202a",
          "#f5cba7",
        ],
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: { width: 200 },
              legend: { position: "bottom" },
            },
          },
        ],
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val.toFixed(2) + "%";
          },
        },
      },
    };
  },

  components: {
    VueApexCharts,
  },

  computed: {
    ...mapGetters(["userInfo"]),

    emptyStateImage() {
      return this.uiState === "error" ? this.IMG_ERROR : this.IMG_EMPTY;
    },

    emptyStateAlt() {
      return this.uiState === "error"
        ? "Se produjo un error con la consulta"
        : "No se encontraron resultados";
    },

    emptyStateTitle() {
      return this.uiState === "error"
        ? "Se produjo un error con la consulta"
        : "Sin datos para el filtro seleccionado";
    },

    emptyStateSubtitle() {
      return this.uiState === "error"
        ? "Inténtalo de nuevo o ajusta los filtros."
        : "Prueba con otro rango de fechas o ciudad.";
    },
  },

  methods: {
    getUrl() {
      var path =
        "/metrics/untrust-projects?client_id=" +
        this.userInfo.user.client_id;

      var url = "";

      if (this.startDate != null && this.endDate != null) {
        var params =
          "&start_date=" + this.startDate + "&end_date=" + this.endDate;
        url = this.url() + path + params;
      } else {
        url = this.url() + path;
      }

      if (this.cityId != null) url += "&city_id=" + this.cityId;

      return url;
    },

    resetChart() {
      this.renderGraphic = false;
      this.series = [];
      this.chartOptions.labels = [];
    },

    setLoading(key) {
      this.requestKey = key;
      this.uiState = "loading";
      this.resetChart();
    },

    setEmpty() {
      this.uiState = "empty";
      this.resetChart();
    },

    setError() {
      this.uiState = "error";
      this.resetChart();
    },

    async setReady(labels, series) {
      // Remount para evitar glitches
      this.renderGraphic = false;
      await this.$nextTick();

      this.chartOptions.labels = labels;
      this.series = series;

      // Si por algún motivo quedó vacío, lo tratamos como empty
      if (!this.series || this.series.length === 0) {
        this.setEmpty();
        return;
      }

      // si la suma es 0 => empty
      var total = 0;
      for (var i = 0; i < this.series.length; i++) total += Number(this.series[i]) || 0;
      if (!total || total <= 0) {
        this.setEmpty();
        return;
      }

      this.renderGraphic = true;
      this.uiState = "ready";
    },

    hasPositiveValues(data) {
      if (!data || typeof data !== "object") return false;

      var keys = Object.keys(data);
      for (var i = 0; i < keys.length; i++) {
        var v = data[keys[i]];
        if (typeof v === "string") v = parseFloat(v);
        if (typeof v === "number" && !isNaN(v) && v > 0) return true;
      }
      return false;
    },

    buildLabelsAndSeries(data) {
      var labels = [];
      var series = [];
      var keys = Object.keys(data);

      for (var i = 0; i < keys.length; i++) {
        var k = keys[i];
        var v = data[k];

        if (typeof v === "string") v = parseFloat(v);
        v = typeof v === "number" && !isNaN(v) ? v : 0;

        if (v > 0) {
          labels.push(this.$t(k));
          series.push(v);
        }
      }

      return { labels: labels, series: series };
    },

    async initializeData(origin) {
      if (!origin) origin = "unknown";

      var key =
        (this.startDate || "") +
        "|" +
        (this.endDate || "") +
        "|" +
        (this.cityId || "");

      this.setLoading(key);

      console.log("[untrust-projects] initializeData:", {
        origin: origin,
        startDate: this.startDate,
        endDate: this.endDate,
        cityId: this.cityId,
        url: this.getUrl(),
      });

      try {
        var request = await this.requestGetWithoutContent(this.getUrl(), {});

        // Ignorar respuestas viejas
        if (this.requestKey !== key) {
          console.log("[untrust-projects] stale response ignored");
          return;
        }

        if (!request || request.status !== 200) {
          console.warn("[untrust-projects] request not ok:", request && request.status);
          this.setError();
          return;
        }

        var data = request.data ? request.data : {};

        var hasAnyPositive = this.hasPositiveValues(data);
        console.log("[untrust-projects] hasAnyPositive:", hasAnyPositive, "data:", data);

        if (!hasAnyPositive) {
          console.warn("[untrust-projects] Empty/zero data");
          this.setEmpty();
          return;
        }

        var built = this.buildLabelsAndSeries(data);

        console.log("[untrust-projects] labels:", built.labels);
        console.log("[untrust-projects] series:", built.series);

        await this.setReady(built.labels, built.series);
      } catch (e) {
        if (this.requestKey !== key) return;
        console.warn("[untrust-projects] request exception:", e);
        this.setError();
      }
    },
  },

  mounted() {
    this.initializeData("mounted");

    this.consultHandler = () =>
      this.initializeData("eventBus:consultData");
    eventBus.$on("consultData", this.consultHandler);
  },

  beforeDestroy() {
    if (this.consultHandler) {
      eventBus.$off("consultData", this.consultHandler);
    }
  },
};
</script>

<style scoped>
.gw-empty-state{
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap: 10px;
  text-align:center;
  padding: 16px;
}

.gw-empty-img{
  max-width: 260px;
  width: 100%;
  height: auto;
}

.gw-empty-title{
  font-weight: 600;
  font-size: 14px;
  color: #4b4b4b;
}

.gw-empty-subtitle{
  font-size: 12px;
  color: #7a7a7a;
}

.gw-spinner{
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid rgba(95,0,145,0.18);
  border-top-color: rgba(95,0,145,0.9);
  animation: gwspin 0.9s linear infinite;
}

@keyframes gwspin{
  to { transform: rotate(360deg); }
}
</style>
